"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function IncredibleHolidaySpecials() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const offerings = [{
    numeral: "I. ESCAPES",
    title: "Tropical Island Getaways",
    description: "Sun-drenched beaches and crystal clear waters await in our curated island paradise collections.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/holiday_beach_resort.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/holiday_beach_resort.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715383/img/holiday_beach_resort.webp-wvc-sizes",
    alt: "A pristine tropical beach with turquoise water and white sand",
    href: "/DestinationsPage"
  }, {
    numeral: "II. ADVENTURE",
    title: "Mountain Expedition Tours",
    description: "Experience the thrill of the peaks with our expert-led trekking and adventure packages.",
    image: "https://margatravel.net/wp-content/uploads/2026/06/PRR_7165-scaled-2.jpg?v=a8zgozd97ha",
    srcset: "https://margatravel.net/wp-content/uploads/2026/06/PRR_7165-scaled-2.jpg?v=r0h28jeu8jp",
    sizes: "https://margatravel.net/wp-content/uploads/2026/06/PRR_7165-scaled-2.jpg?v=50y8fuwq4g5",
    alt: "A breathtaking view of snow-capped mountain ranges",
    href: "/DestinationsPage"
  }, {
    numeral: "III. CULTURE",
    title: "Historic City Breaks",
    description: "Immerse yourself in the rich heritage and vibrant street life of the world's most iconic cities.",
    image: "https://margatravel.net/wp-content/uploads/2026/06/ushaka-marine-world_1-1.jpg?v=l06xjrvpq6l",
    srcset: "https://margatravel.net/wp-content/uploads/2026/06/ushaka-marine-world_1-1.jpg?v=kjz2vfidyw",
    sizes: "https://margatravel.net/wp-content/uploads/2026/06/ushaka-marine-world_1-1.jpg?v=cssvr11k6ve",
    alt: "A bustling historic city street with classic architecture",
    href: "/DestinationsPage"
  }];
  return <section data-section-id="3273" id="incredible-holiday-specials" className="relative py-24 md:py-32 lg:py-40 overflow-hidden bg-background text-foreground">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        {}
        <div ref={header.ref} className={`text-center max-w-3xl mx-auto transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default text-xs md:text-sm tracking-[0.25em] uppercase mb-8 text-primary">— &nbsp;HOLIDAY SPECIALS</p>
          <h2 className="font-default font-semibold text-[2.25rem] sm:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight text-foreground">
            Incredible holiday specials to be experienced.
          </h2>
          <p className="mt-8 text-base md:text-[17px] leading-relaxed font-default text-foreground/80">
            Discover our hand-picked travel packages designed for unforgettable journeys.
          </p>
        </div>

        {}
        <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-10">
          {offerings.map((item, i) => {
          return <OfferingCard key={i} index={i} numeral={item.numeral} title={item.title} description={item.description} image={item.image} srcset={item.srcset} sizes={item.sizes} alt={item.alt} href={item.href} showDivider={i < offerings.length - 1} />;
        })}
        </div>
      </div>
    </section>;
}
function OfferingCard({
  index,
  numeral,
  title,
  description,
  image,
  srcset,
  sizes,
  alt,
  href,
  showDivider
}: {
  index: number;
  numeral: string;
  title: string;
  description: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
  href: string;
  showDivider: boolean;
}) {
  const reveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div data-index={index} ref={reveal.ref} className={`relative transition-all duration-1000 ease-out ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 140}ms`
  }}>
      <Link to={href} className="group block transition-transform duration-700 ease-out hover:-translate-y-1.5" aria-label={`Explore ${title}`}>
        {}
        <div className="relative overflow-hidden rounded-lg shadow-md" style={{
        aspectRatio: "5 / 7"
      }}>
          <img src={image} data-wvc-srcset={srcset} data-wvc-sizes={sizes} alt={alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]" />
          <div className="absolute inset-0 opacity-0 transition-opacity duration-700 ease-out group-hover:opacity-100 bg-gradient-to-t from-black/40 to-transparent" />
        </div>

        {}
        <p className="mt-8 font-default text-xs tracking-[0.25em] uppercase text-primary">
          {numeral}
        </p>

        {}
        <h3 className="mt-4 font-default font-semibold text-[1.625rem] md:text-[1.75rem] leading-tight tracking-tight text-foreground">
          {title}
        </h3>

        {}
        <p className="mt-4 text-[15px] leading-relaxed font-default max-w-md text-foreground/70">
          {description}
        </p>

        {}
        <div className="mt-6 inline-flex items-center gap-2 font-default text-xs tracking-[0.18em] uppercase text-primary">
          <span>Explore the package</span>
          <ArrowRight className="h-4 w-4 transition-transform duration-500 ease-out group-hover:translate-x-1.5" strokeWidth={1.25} />
        </div>
      </Link>

      {}
      {showDivider && <div className="md:hidden mt-10 h-px w-full bg-border" aria-hidden="true" />}
    </div>;
}